import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, shareReplay, switchMap, throwError, finalize } from 'rxjs';
import { Auth } from '../services/auth';
import { AuthApiService } from '../services/auth-api.service';
import { ApiEnvelope } from './auth-envelope';
import { AuthSession } from '../models/auth.model';

// Only login/refresh skip the bearer header — everything else in
// planificacion_fase_2.md is Authorization-protected, and attaching it to
// other domains' (unrelated, unauthenticated) endpoints is harmless.
const NO_AUTH_HEADER_PATHS = ['/v1/auth/login', '/v1/auth/refresh'];

// These already clear local state unconditionally at their own call site
// regardless of the server's response (see planificacion_fase_2.md), so a
// 401 from them shouldn't trigger the refresh-and-retry dance.
const NO_REFRESH_RETRY_PATHS = [
  '/v1/auth/login',
  '/v1/auth/refresh',
  '/v1/auth/logout',
  '/v1/auth/cerrar_todas_las_sesiones',
];

// The spec's refresh-retry is specifically for "401 por access token
// vencido" — not every 401. Several endpoints use 401 for a domain-specific
// rejection with a perfectly valid, unexpired token (e.g. PUT /v1/auth/password
// returns 401 INVALID_CURRENT_PASSWORD for a mistyped current password).
// Retrying those would silently rotate the session as a side effect of
// unrelated input validation, so only these error codes count as "the token
// itself is the problem."
const RETRYABLE_401_CODES = ['AUTHENTICATION_REQUIRED', 'SESSION_REVOKED', 'SESSION_EXPIRED'];

function matchesPath(url: string, paths: string[]): boolean {
  return paths.some((path) => url.includes(path));
}

function isRetryable401(error: unknown, requestUrl: string): error is HttpErrorResponse {
  if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
    return false;
  }
  if (matchesPath(requestUrl, NO_REFRESH_RETRY_PATHS)) {
    return false;
  }
  const body = error.error as ApiEnvelope<unknown> | undefined;
  return RETRYABLE_401_CODES.includes(body?.error?.code ?? '');
}

// Module-level so concurrent 401s across different requests share a single
// in-flight refresh call instead of each firing their own — reset once it
// settles so the next 401 (after this one resolves) starts a fresh attempt.
let refreshInFlight$: Observable<AuthSession> | null = null;

function refreshSession(authApi: AuthApiService, auth: Auth): Observable<AuthSession> {
  if (!refreshInFlight$) {
    const refreshToken = auth.refreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }
    refreshInFlight$ = authApi.refresh(refreshToken).pipe(
      map((session) => {
        auth.applySession(session);
        return session;
      }),
      shareReplay(1),
      finalize(() => {
        refreshInFlight$ = null;
      }),
    );
  }
  return refreshInFlight$;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(Auth);
  const authApi = inject(AuthApiService);
  const router = inject(Router);

  const accessToken = auth.accessToken();
  const authedReq =
    accessToken && !matchesPath(req.url, NO_AUTH_HEADER_PATHS)
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

  return next(authedReq).pipe(
    catchError((error: unknown) => {
      if (!isRetryable401(error, req.url)) {
        return throwError(() => error);
      }

      // catchError here only ever sees refreshSession's own failure — the
      // retried next() call below is outside its scope, so a second 401 from
      // the retry just propagates as a normal error instead of forcing
      // another (looping) refresh or an unwarranted logout.
      return refreshSession(authApi, auth).pipe(
        catchError((refreshError: unknown) => {
          auth.clear();
          router.navigateByUrl('/app/login');
          return throwError(() => refreshError);
        }),
        switchMap((session) => next(req.clone({ setHeaders: { Authorization: `Bearer ${session.accessToken}` } }))),
      );
    }),
  );
};
